import React from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import DownloadButton from "../components/DownloadButton";
import { BRAND, siteUrl } from "../utils/config";

const InfoPage = ({ path, title, description, h1, intro, blocks, cta }) => {
  const origin = siteUrl();
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
        { "@type": "ListItem", position: 2, name: h1, item: `${origin}${path}` },
      ],
    },
  ];
  return (
    <main className="rt-main">
      <Seo title={title} description={description} path={path} schema={schema} />
      <section className="rt-phead">
        <div className="rt-wrap">
          <nav className="rt-crumbs" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span>/</span>
            <b>{h1}</b>
          </nav>
          <h1>{h1}</h1>
          {intro && <p>{intro}</p>}
        </div>
      </section>
      <section className="rt-wrap rt-section">
        <article className="rt-article">
          {blocks.map((b) => (
            <section key={b.h}>
              <h2>{b.h}</h2>
              {(b.p || []).map((t, i) => (
                <p key={i}>{t}</p>
              ))}
              {b.list && (
                <ul>
                  {b.list.map((li) => (
                    <li key={li}>{li}</li>
                  ))}
                </ul>
              )}
              {b.steps && (
                <ol className="rt-article__steps">
                  {b.steps.map((s) => (
                    <li key={s.t}>
                      <strong>{s.t}</strong>
                      <span>{s.d}</span>
                    </li>
                  ))}
                </ol>
              )}
            </section>
          ))}
        </article>
        {cta && (
          <div className="rt-cta-row">
            <DownloadButton />
          </div>
        )}
      </section>
    </main>
  );
};

export const About = () => (
  <InfoPage
    path="/about"
    title={`About ${BRAND.name} | Live Matka Results & Panel Charts`}
    description={`About ${BRAND.name}: a clean, mobile-friendly place for live matka results, Jodi charts and Pana charts of every market.`}
    h1="About Us"
    intro={`${BRAND.name} publishes live matka market results and complete historical charts in one clean, mobile-friendly place.`}
    cta
    blocks={[
      {
        h: "Who we are",
        p: [
          `${BRAND.name} brings live matka market results and complete historical panel charts together on one fast website. Every market's open pana, jodi and close pana is shown as soon as it is declared, so you never have to refresh a dozen different pages.`,
        ],
      },
      {
        h: "What you get",
        list: [
          "Live results — every market on a single screen with its open or closed status.",
          "Jodi charts — week-by-week two-digit records for every market.",
          "Pana charts — the full open, jodi and close panel history.",
          "Fast and responsive — works smoothly on phone, tablet and desktop.",
        ],
      },
      {
        h: "How results reach this site",
        p: [
          "All market data on this website is loaded automatically from our results service. Nothing is typed in by hand, so what you see is what the source publishes — without delays or manual edits.",
        ],
      },
      {
        h: "Contact",
        p: ["For any help, message us on WhatsApp. Support is available 24/7."],
      },
      {
        h: "Important notice",
        p: [
          "This website publishes result data for information only. Gambling laws differ from state to state and country to country — please check the rules where you live before taking part. The site is strictly for users aged 18 and above. Play responsibly and never wager money you cannot afford to lose.",
        ],
      },
    ]}
  />
);

export const HowToPlay = () => (
  <InfoPage
    path="/how-to-play"
    title={`How to Play Matka Online | ${BRAND.name} App Guide`}
    description={`Step-by-step guide to download the ${BRAND.name} app, add points, choose a market, pick your numbers and read matka charts.`}
    h1="How to Play"
    intro="Download the app, add points, pick a market and numbers — it takes only a few minutes."
    cta
    blocks={[
      {
        h: "Get started in 5 steps",
        steps: [
          { t: "1. Download the app", d: `Tap Download App and install the ${BRAND.name} Android app (APK).` },
          { t: "2. Create your account", d: "Register with your mobile number and log in." },
          { t: "3. Add points", d: "Add points to your wallet using the payment options shown in the app." },
          { t: "4. Choose a market and game", d: "Pick an open market, then Single, Jodi, Patti or Sangam, and enter your numbers and amount." },
          { t: "5. Check results and withdraw", d: "Results appear on this website and in the app. Winnings go to your wallet and can be withdrawn." },
        ],
      },
      {
        h: "Game types",
        list: [
          "Single — pick one digit from 0 to 9.",
          "Jodi — pick a two-digit number from 00 to 99.",
          "Single, Double and Triple Patti (Pana) — pick a three-digit panna.",
          "Half Sangam and Full Sangam — combine a pana with a digit or with a jodi.",
        ],
      },
      {
        h: "Reading the charts",
        p: [
          "Open any market from the All Charts page. Rows are weeks and columns are Monday to Sunday. A Jodi chart shows the two-digit result; a Pana chart shows the open pana, jodi and close pana.",
        ],
      },
      {
        h: "Play responsibly",
        p: ["Only play if you are 18 or older and the law where you live allows it. Never bet money you cannot afford to lose."],
      },
    ]}
  />
);

export const Terms = () => (
  <InfoPage
    path="/terms"
    title={`Terms & Conditions | ${BRAND.name}`}
    description={`Terms and conditions for using the ${BRAND.name} website: age limit, legal compliance, accuracy of results and responsible play.`}
    h1="Terms & Conditions"
    intro="Please read these terms before using this website."
    blocks={[
      { h: "1. Age restriction", p: ["This website is strictly for people aged 18 years or above. By using it you confirm that you meet this requirement."] },
      {
        h: "2. Legal compliance",
        p: ["Gambling and betting laws vary by state and country and are restricted or prohibited in many places. You alone are responsible for knowing and following the laws that apply to you. Do not use this website where it is not permitted."],
      },
      { h: "3. Information only", p: ["All results, charts and timings are published for information. This website does not take bets, hold funds or process payments."] },
      {
        h: "4. Accuracy",
        p: ["Results are loaded automatically from our results service. We work to keep them accurate and current but cannot promise that data is error-free or always available, and we accept no liability for loss caused by relying on it."],
      },
      { h: "5. No guarantees", p: ["Nothing on this website is a prediction, tip or promise of any outcome or earnings. Past results do not indicate future results."] },
      { h: "6. Play responsibly", p: ["Never spend money you cannot afford to lose. If your play becomes a problem, stop and seek help."] },
      { h: "7. Intellectual property", p: [`The design, layout and content of this website belong to ${BRAND.name} and may not be copied without permission.`] },
      { h: "8. Changes", p: ["We may update these terms at any time. Continued use of the website means you accept the updated version."] },
    ]}
  />
);

export const Privacy = () => (
  <InfoPage
    path="/privacy"
    title={`Privacy Policy | ${BRAND.name}`}
    description={`How ${BRAND.name} handles information: what is collected, cookies and your choices.`}
    h1="Privacy Policy"
    intro="How this website handles information."
    blocks={[
      {
        h: "Information we collect",
        p: ["You do not need an account to use this website. We may collect basic technical data such as device type, pages visited and approximate location through analytics tools, to understand how the site is used."],
      },
      { h: "How we use it", list: ["To keep the website working and improve it", "To measure how the site performs", "To answer support messages"] },
      { h: "Cookies", p: ["Analytics or advertising partners may use cookies and similar technologies. You can control cookies in your browser settings."] },
      { h: "Your choices", p: ["You can ask us to remove data we hold about you by messaging support on WhatsApp."] },
    ]}
  />
);

export const Disclaimer = () => (
  <InfoPage
    path="/disclaimer"
    title={`Disclaimer | ${BRAND.name}`}
    description={`Important disclaimer for visitors of ${BRAND.name}: information only, legal responsibility and risk.`}
    h1="Disclaimer"
    intro="Please read this notice before using the website."
    blocks={[
      { h: "Information only", p: ["Charts, records and results are shown for information. They are not advice, and past results do not predict future results."] },
      { h: "Risk and legality", p: ["Playing matka involves financial risk and may be restricted or illegal in some places. You are responsible for following the law where you live. You must be 18 or older."] },
      { h: "External links", p: ["Links to other websites or downloads are provided for convenience. We are not responsible for their content."] },
    ]}
  />
);
